import clsx from 'clsx'
import { ArrowUpRight, Search } from '@/assets/icons'
import { SearchSuggestionRow } from './SearchSuggestionRow'
import { dropdownFrameClassName, dropdownSectionHeaderClassName } from './dropdown-styles'
import type { SearchSuggestion } from '../hooks/useSearchSuggestions'

type SearchSuggestionsProps = {
  /** Rendered first, above the folder section. */
  files: SearchSuggestion[]
  folders: SearchSuggestion[]
  /** The live query, so each row can bold its matched substring. */
  query: string
  /** Index into `[...files, ...folders]`, the "All search results" row after them, or -1. */
  activeIndex: number
  onActiveIndexChange: (index: number) => void
  onSelect: (suggestion: SearchSuggestion) => void
  /** Commits the query, from the "All search results" row. */
  onRunSearch: () => void
  /** Referenced by the input's `aria-controls`. */
  listboxId: string
  /** Builds the row id the input points `aria-activedescendant` at. */
  getOptionId: (index: number) => string
}

type SectionProps = {
  label: string
  headerId: string
  suggestions: SearchSuggestion[]
  /** Where this section starts in the flattened list the arrow keys walk. */
  indexOffset: number
} & Pick<SearchSuggestionsProps, 'query' | 'activeIndex' | 'onActiveIndexChange' | 'onSelect' | 'getOptionId'>

function SearchSuggestionSection({ label, headerId, suggestions, indexOffset, query, activeIndex, onActiveIndexChange, onSelect, getOptionId }: SectionProps) {
  if (suggestions.length === 0) return null

  return (
    <div role="group" aria-labelledby={headerId}>
      <div id={headerId} className={`flex h-8 items-center px-2 ${dropdownSectionHeaderClassName}`}>
        {label}
      </div>

      {suggestions.map((suggestion, index) => {
        const flatIndex = indexOffset + index

        return (
          <SearchSuggestionRow
            key={suggestion.id}
            suggestion={suggestion}
            query={query}
            id={getOptionId(flatIndex)}
            active={flatIndex === activeIndex}
            onSelect={() => onSelect(suggestion)}
            onHover={() => onActiveIndexChange(flatIndex)}
          />
        )
      })}
    </div>
  )
}

/**
 * The typeahead dropdown under the matter files search field.
 *
 * Two sections — files, then folders — mirroring the two endpoints behind them — and a last row
 * that runs the full search, present even when nothing matched so the dropdown never goes blank on
 * a live query. It is a plain positioned element rather than a menu portal because focus has to
 * stay in the input: this is a combobox, so the input keeps focus and points
 * `aria-activedescendant` at the active row.
 */
export function SearchSuggestions({
  files,
  folders,
  query,
  activeIndex,
  onActiveIndexChange,
  onSelect,
  onRunSearch,
  listboxId,
  getOptionId,
}: SearchSuggestionsProps) {
  const sectionProps = { query, activeIndex, onActiveIndexChange, onSelect, getOptionId }
  const hasSuggestions = files.length + folders.length > 0
  const runSearchIndex = files.length + folders.length
  const isRunSearchActive = activeIndex === runSearchIndex

  return (
    <div id={listboxId} role="listbox" aria-label="File and folder suggestions" className={dropdownFrameClassName}>
      {hasSuggestions && (
        // Only the suggestions scroll, so the last row stays in view however long the list is.
        <div className="min-h-0 overflow-y-auto p-2">
          <SearchSuggestionSection label="Files" headerId={`${listboxId}-files`} suggestions={files} indexOffset={0} {...sectionProps} />
          <SearchSuggestionSection label="Folders" headerId={`${listboxId}-folders`} suggestions={folders} indexOffset={files.length} {...sectionProps} />
        </div>
      )}

      <div className={clsx('shrink-0 p-2', hasSuggestions && 'border-t border-brand-neutral-200')}>
        <div
          id={getOptionId(runSearchIndex)}
          role="option"
          aria-selected={isRunSearchActive}
          className={clsx('flex h-10 w-full cursor-pointer items-center gap-2 rounded p-2 text-left', isRunSearchActive && 'bg-brand-neutral-100')}
          // Mousedown would blur the input and close the dropdown before the click lands.
          onMouseDown={(event) => event.preventDefault()}
          onClick={onRunSearch}
          onMouseEnter={() => onActiveIndexChange(runSearchIndex)}
        >
          <Search className={clsx('h-4 w-4 shrink-0', isRunSearchActive ? 'text-brand-neutral-800' : 'text-brand-neutral-600')} aria-hidden />
          <span
            className={clsx(
              'min-w-0 flex-1 truncate text-sm leading-5 tracking-[-0.15px]',
              isRunSearchActive ? 'text-brand-neutral-800' : 'text-brand-neutral-700'
            )}
          >
            All search results for <span className="font-semibold text-brand-neutral-950">“{query.trim()}”</span>
          </span>
          <ArrowUpRight className={clsx('h-4 w-4 shrink-0', isRunSearchActive ? 'text-brand-neutral-800' : 'text-brand-neutral-600')} aria-hidden />
        </div>
      </div>
    </div>
  )
}
